import React, { FC, Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';

import { calculateShots } from '../../../../utils/score-calculator';
import { Button, FlexColumnCentred, FlexRowSpaceAround } from '../../../common-styles/common-styles';
import { Course, Player, PlayerResult, TeamGameMatch, TeamGameMatchTeam } from '../../../../../database.schema';
import * as CSS from '../../match-entry-styles';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../../../../app/constants';
import { useFetchPlayers } from '../../../../api/hooks/use-fetch-players';
import { TeamGameHandicapEntryConfirmation } from './team-game-handicap-entry-confirmation';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 1rem;
  position: relative;
`;

const CourseWrapper = styled.h3`
  margin: 1rem 0;
`;

export const CheckboxInput = styled.input`
  margin: 0 0.5rem;
`;

type TeamGameHandicapEntryProps = {
  editing: boolean;
  startingHole: number;
  setRecordedMatch: (value: TeamGameMatch) => void;
  setShowHandicapEntry: (value: boolean) => void;
  recordedMatch: TeamGameMatch;
  course: Course;
  matchIndex: number;
  teamIndex: number;
};

export const TeamGameHandicapEntry: FC<TeamGameHandicapEntryProps> = ({
  startingHole,
  setRecordedMatch,
  recordedMatch,
  course,
  setShowHandicapEntry,
  editing,
  teamIndex,
}): JSX.Element => {
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const { data } = useFetchPlayers();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [recordedMatchStore, setRecordedMatchStore] = useState<TeamGameMatch>(recordedMatch);
  const [tierMatchTeam, setTierMatchTeam] = useState<TeamGameMatchTeam>(recordedMatch.teams[teamIndex]);

  const adjustCourseScoresToHandicap = (handicap: number): number[] | undefined => {
    return holeIndexes.map((holeIndex: number) => {
      return course.scoreCard[holeIndex] + calculateShots(handicap, course.strokeIndex[holeIndex]);
    });
  };

  const players = data as Array<Player>;

  useEffect(() => {
    const teamPlayers: PlayerResult[] = [];
    tierMatchTeam.players.forEach((player: PlayerResult) => {
      if (player.round) {
        const individualHandicap = player.round.individualHandicap === -1 ? 18 : player.round.individualHandicap;
        const adjustedCourseScoreCard = adjustCourseScoresToHandicap(Number(individualHandicap));
        const longestDrive = player.longestDrive ? player.longestDrive : 0;
        const nearestThePin = player.nearestThePin ? player.nearestThePin : 0;
        if (editing) {
          teamPlayers.push({ ...player });
        } else {
          if (adjustedCourseScoreCard) {
            teamPlayers.push({ ...player, longestDrive, nearestThePin, round: { ...player.round, courseHandicap: 0, individualHandicap, scoreCard: adjustedCourseScoreCard } });
          }
        }
      }
    });
    setTierMatchTeam({ ...tierMatchTeam, players: teamPlayers });
  }, [recordedMatch, editing]);

  const allHandicapsHaveBeenEntered = () => {
    return tierMatchTeam.players.every((player: PlayerResult) => player.round.individualHandicap > 0);
  };
  const getPlayer = (playerId: number) => {
    return players.find((player: Player) => player.id === playerId);
  };

  const setIndividualHandicapForPlayer = (index: number, individualHandicap: number) => {
    if (individualHandicap && course) {
      const adjustedCourseScoreCard = adjustCourseScoresToHandicap(Number(individualHandicap));
      if (adjustedCourseScoreCard) {
        const teamPlayers = [...tierMatchTeam.players];
        const adjustedPlayerRound: PlayerResult = {
          ...teamPlayers[index],
          round: {
            scoreCard: editing ? teamPlayers[index].round.scoreCard : adjustedCourseScoreCard,
            courseHandicap: teamPlayers[index].round.courseHandicap,
            individualHandicap,
            shotsReceived: teamPlayers[index].round.shotsReceived,
            playerId: -1,
            points: 0,
          },
        };
        teamPlayers.splice(index, 1, { ...adjustedPlayerRound });
        setTierMatchTeam({ ...tierMatchTeam, players: teamPlayers });
      }
    }
  };

  const setLongestDriveForPlayer = (index: number, longestDrive: number) => {
    const teamPlayers = [...tierMatchTeam.players];
    const adjustedPlayerRound: PlayerResult = {
      ...teamPlayers[index],
      longestDrive,
    };
    teamPlayers.splice(index, 1, { ...adjustedPlayerRound });
    setTierMatchTeam({ ...tierMatchTeam, players: teamPlayers });
  };

  const setNearestThePinForPlayer = (index: number, nearestThePin: number) => {
    const teamPlayers = [...tierMatchTeam.players];
    const adjustedPlayerRound: PlayerResult = {
      ...teamPlayers[index],
      nearestThePin,
    };
    teamPlayers.splice(index, 1, { ...adjustedPlayerRound });
    setTierMatchTeam({ ...tierMatchTeam, players: teamPlayers });
  };

  const decrementIndividualHandicap = (index: number, handicap: number) => {
    const newHandicap = handicap - 1;
    setIndividualHandicapForPlayer(index, newHandicap);
  };
  const incrementIndividualHandicap = (index: number, handicap: number) => {
    const newHandicap = handicap + 1;
    setIndividualHandicapForPlayer(index, newHandicap);
  };

  if (showConfirm) {
    const updatedRecordedMatch = { ...recordedMatchStore };
    updatedRecordedMatch.teams.splice(teamIndex, 1, tierMatchTeam);
    return (
      <div>
        <TeamGameHandicapEntryConfirmation recordedMatch={updatedRecordedMatch} course={course} players={players} teamIndex={teamIndex} />
        <CSS.NavigationWrapper>
          <CSS.FlexRow>
            <CSS.NavigationButton
              onClick={() => {
                setShowConfirm(false);
              }}
            >
              Back
            </CSS.NavigationButton>
            <CSS.NavigationButton
              data-testid="confirm-button"
              onClick={() => {
                setRecordedMatch({ ...updatedRecordedMatch });
                setShowHandicapEntry(false);
              }}
            >
              Confirm
            </CSS.NavigationButton>
          </CSS.FlexRow>
        </CSS.NavigationWrapper>
      </div>
    );
  }

  return (
    <Fragment>
      <h2>Handicap Entry</h2>
      <CourseWrapper>Course: {course.name}</CourseWrapper>
      <FlexColumnCentred>
        <CSS.FlexRow>
          <CSS.PlayerNameTitle>Player</CSS.PlayerNameTitle>
          <CSS.PlayerNameTitle>Handicap</CSS.PlayerNameTitle>
          <CSS.PlayerNameTitleX>Longest Drive</CSS.PlayerNameTitleX>
          <CSS.PlayerNameTitle>Nearest The Pin</CSS.PlayerNameTitle>
        </CSS.FlexRow>
        <CSS.FlexColumn data-testid="handicap-entry" style={{ border: '2px solid red', borderRadius: '5px', margin: ' 0 0 1rem', padding: '0.5rem 0' }}>
          {tierMatchTeam.players.map((player: PlayerResult, index: number) => (
            <FlexRowSpaceAround key={`${player.playerId}`}>
              <CSS.PlayerNameTitle>{getPlayer(player.playerId ?? 1)?.nickName}</CSS.PlayerNameTitle>
              <CSS.PlayerNameTitle>
                <CSS.CrementWrapper>
                  <CSS.FlexRow>
                    <CSS.CrementButton data-testid={`t1-decrement-${index}`} onClick={() => decrementIndividualHandicap(index, player.round.individualHandicap)}>
                      -
                    </CSS.CrementButton>
                    <CSS.HoleInfo>{player.round.individualHandicap}</CSS.HoleInfo>
                    <CSS.CrementButton data-testid={`t1-increment-${index}`} onClick={() => incrementIndividualHandicap(index, player.round.individualHandicap)}>
                      +
                    </CSS.CrementButton>
                  </CSS.FlexRow>
                </CSS.CrementWrapper>
              </CSS.PlayerNameTitle>
              <CSS.PlayerNameTitle>
                <CSS.CrementWrapper>
                  <CSS.FlexRow>
                    <CheckboxInput
                      type="checkbox"
                      name="longestDrive"
                      id="tier-points"
                      data-testid="tier-points-type"
                      checked={player.longestDrive > 0}
                      onChange={() => setLongestDriveForPlayer(index, player.longestDrive === 0 ? 1 : 0)}
                    />
                  </CSS.FlexRow>
                </CSS.CrementWrapper>
              </CSS.PlayerNameTitle>
              <CSS.PlayerNameTitle>
                <CSS.CrementWrapper>
                  <CSS.FlexRow>
                    <CheckboxInput
                      type="checkbox"
                      name="nearestThePin"
                      id="tier-points"
                      data-testid="tier-points-type"
                      checked={player.nearestThePin > 0}
                      onChange={() => setNearestThePinForPlayer(index, player.nearestThePin === 0 ? 1 : 0)}
                    />
                  </CSS.FlexRow>
                </CSS.CrementWrapper>
              </CSS.PlayerNameTitle>
            </FlexRowSpaceAround>
          ))}
        </CSS.FlexColumn>
      </FlexColumnCentred>
      <div>Stableford 95% of handicap</div>
      <ButtonWrapper>
        <Button data-testid="continue-button" onClick={() => setShowConfirm(true)} disabled={!Boolean(allHandicapsHaveBeenEntered())}>
          Continue
        </Button>
      </ButtonWrapper>
    </Fragment>
  );
};
